import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Link from "../../../link/link";
import { Link as RouterLink } from "react-router-dom";
import ErrorBoundary from "../../../../error-boundary";
import { teaserNewsPropTypes } from "../../../teaser-base/news/teaser-news";
import { teaserEventPropTypes } from "../../../teaser-base/event/teaser-event";
import { teaserPersonPropTypes } from "../../../teaser-base/person/teaser-person";
import { teaserGeneralPropTypes } from "../../../teaser-base/general/teaser-general";
import { teaserProjectPropTypes } from "../../../teaser-base/project/teaser-project";
import { teaserActPropTypes } from "../../../teaser-base/act/teaser-act";
import ComponentTeaserListCarousel from "./components/component-teaser-list-carousel";
import ComponentTeaserListWrap from "./components/component-teaser-list-wrap";
import CtaElement from "@default/general-components/cta-element";

/**
 * Extend for new content types as needed. Therefore copy to
 * src/js/general-general-components/content-base/paragraphs/teaserliste/<content_type>/paragraphs-teaser-list<content_type>.jsx
 * and change/remove/add as needed.
 */
const ParagraphTeaserList = ({ content }) => {
  let type =
      content.fieldContentType ||
      content.entityBundle.replace(/teaser_list_/g, "").replace(/_/g, "-"),
    participantType = "";

  // If Type List is showing Participant types we cannot search for the type, we need to search for node type.
  if (type === "acts" || type === "speakers" || type === "moderators") {
    participantType = type.substring(0, type.length - 1);
    type = "participant";
  }

  const isCarousel =
      content.entityBundle === "teaser_list_news" ||
      content.fieldDisplayMode === "carousel",
    sectionClassNames = classNames({
      "paragraph paragraph-teaser-list": true,
      [`paragraph-teaser-list-${content.fieldLimit}`]: true,
      "paragraph paragraph-teaser-list-slick": isCarousel,
      "paragraph paragraph-teaser-list-wrap": !isCarousel,
      [`paragraph-teaser-list-${
        type === "news" ? content.fieldNewsType : type
      } ${participantType}`]: true,
    }),
    titleClassNames = classNames({
      "headline-4xl": content.entityBundle === "teaser_list_news",
      "headline-xl":
        content.entityBundle === "teaser_list_acts" && !content.fieldBig,
      "headline-xxl":
        content.entityBundle === "teaser_list_acts" && content.fieldBig,
      "headline-l": content.fieldNewsType === "press",
    });

  return (
    <section
      className={sectionClassNames}
      data-darkmode={content.fieldDarkMode}
    >
      {content.fieldHeading && (
        <div className="container">
          <div className="row">
            <div className="col-12 heading">
              <h2 className={titleClassNames}>{content.fieldHeading}</h2>
            </div>
          </div>
        </div>
      )}

      {isCarousel && (
        <ErrorBoundary>
          <ComponentTeaserListCarousel
            manualNodes={content.fieldNodes}
            id={content.id}
            mode={content.fieldMode}
            limit={content.fieldLimit || 100}
            type={type}
            newsType={content.fieldNewsType}
            participantType={participantType}
            pagerFullPage={content.fieldPagerAufVollseiten ? content.id : false}
            pagerFullPageOverviewLink={content.fieldLinkOverview}
            tags={content.fieldTags}
            subject={content.fieldSubject}
            faculty={content.fieldFaculty}
            status={content.fieldStatus}
          />
        </ErrorBoundary>
      )}

      {content.fieldDisplayMode === "wrap" &&
        content.fieldNewsType !== "press" && (
          <div className="container">
            <div className="row">
              <ErrorBoundary>
                <ComponentTeaserListWrap
                  manualNodes={content.fieldNodes}
                  id={content.id}
                  mode={content.fieldMode}
                  limit={content.fieldLimit}
                  type={type}
                  participantType={participantType}
                  pagerFullPage={
                    content.fieldPagerAufVollseiten ? content.id : false
                  }
                  pagerFullPageOverviewLink={content.fieldLinkOverview}
                  tags={content.fieldTags}
                />
              </ErrorBoundary>
            </div>
          </div>
        )}

      {content.fieldLinkOverview?.uri?.path &&
        content.fieldLinkOverview?.title && (
          <CtaElement link={content.fieldLinkOverview} />
        )}
    </section>
  );
};

ParagraphTeaserList.propTypes = {
  viewMode: PropTypes.string,
  content: PropTypes.shape({
    id: PropTypes.string,
    fieldDisplayMode: PropTypes.oneOf(["wrap", "carousel"]),
    fieldLimit: PropTypes.number,
    fieldPagerAufVollseiten: PropTypes.bool,
    fieldContentType: PropTypes.oneOf([
      "news",
      "person",
      "event",
      "all",
      "acts",
      "speakers",
    ]),
    fieldHeading: PropTypes.string,
    fieldTags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
    fieldMode: PropTypes.oneOf(["auto", "manual"]),
    fieldLinkOverview: PropTypes.shape({
      title: PropTypes.string,
      uri: PropTypes.shape({
        path: PropTypes.string,
        __typename: PropTypes.string,
      }),
    }),
    fieldNodes: PropTypes.arrayOf(
      PropTypes.shape({
        teaserNewsPropTypes,
        teaserEventPropTypes,
        teaserPersonPropTypes,
        teaserGeneralPropTypes,
        teaserProjectPropTypes,
        teaserActPropTypes,
      })
    ),
  }),
};

export default ParagraphTeaserList;
